import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Grid,
  Card,
  CardMedia,
  TextField,
  Dialog,
  DialogContent,
  IconButton
} from '@mui/material';
import { CheckCircle, Cancel, Warning, Help, Close } from '@mui/icons-material';
import api, { apiMultiPart } from '../../../services/api';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxWidth: '90vw',
  maxHeight: '90vh',
  overflowY: 'auto'
};

const mockDocuments = [
  { id: 1, url: 'https://cdn.pixabay.com/photo/2016/02/14/00/13/brake-1198862_1280.jpg', title: 'ID Document' },
  { id: 2, url: 'https://cdn.pixabay.com/photo/2019/05/09/10/52/audit-4190944_640.jpg', title: 'Bank Statement' },
  { id: 3, url: 'https://cdn.pixabay.com/photo/2017/11/13/11/45/checklist-2945401_640.jpg', title: 'Nonprofit Certification' },
  { id: 4, url: 'https://cdn.pixabay.com/photo/2014/07/06/13/55/calculator-385506_1280.jpg', title: 'Tax Document' },
];

const VerifyCampaignModal = ({ open, onClose, campaign, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [action, setAction] = useState(null);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [attachmentPaths, setAttachmentPaths] = useState([]);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    // if (!ticket) return null;
    if (open) {
      fetchAttachments();
    }
  }, [open]);

  const fetchAttachments = async () => {
    try {
      const response = await api.get(`/v1/api/campaigns/request-verification/attachment-paths/${campaign.id}`);
      // setAttachmentPaths(response.data);

      const promises = response?.data.map(path => apiMultiPart.get(`/v1/api/campaigns/request-verification/attachment/${campaign.id}?path=${path}`, { responseType: 'blob' }));
      const responses = await Promise.all(promises);
      const attachmentData = responses.map(response => URL.createObjectURL(response.data));
      setAttachments(attachmentData);
    } catch (error) {
      console.error('Failed to load attachment paths:', error);
    }
  };


  const handleVerify = async () => {
    if (!campaign || !action) return;

    if ((action === 'reject' || action === 'more-info') && !comment.trim()) {
      setCommentError(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.put(`/v1/api/campaigns/request-verification/campaign/${campaign.id}/verify`, {
        approved: action === 'approve',
        needsMoreInfo: action === 'more-info',
        comment: action !== 'approve' ? comment : undefined
      });

      onSuccess(
        action === 'approve' ? 'approved' :
          action === 'reject' ? 'rejected' :
            'more-info-requested'
      );
      onClose();
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.message ||
        err.message ||
        `Failed to process campaign. Please try again later.`);
    } finally {
      setLoading(false);
    }
  };

  const handleActionChange = (newAction) => {
    setAction(newAction);
    setCommentError(false);
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    //setOpenImageDialog(true);
  };
  // const handleImageClick = (image) => {
  //   setSelectedImage(image);
  // };

  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="verify-modal-title"
      >
        <Box sx={style}>
          <Typography id="verify-modal-title" variant="h6" component="h2" gutterBottom>
            Campaign Verification: {campaign?.title}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Campaign Details</strong>
              </Typography>

              <Typography variant="body2" gutterBottom>
                <strong>Category:</strong> {campaign?.category}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Location:</strong> {campaign?.location}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Goal Amount:</strong> ${campaign?.goalAmount?.toLocaleString()}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Amount Raised:</strong> ${campaign?.amountRaised?.toLocaleString()}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>End Date:</strong> {campaign?.endDate} ({campaign?.daysLeft > 0 ? `${campaign.daysLeft} days left` : 'Ended'})
              </Typography>

              <Typography variant="subtitle1" sx={{ mt: 2 }} gutterBottom>
                <strong>Description</strong>
              </Typography>
              <Typography variant="body2" gutterBottom>
                {campaign?.description}
              </Typography>

              <Typography variant="subtitle1" sx={{ mt: 2 }} gutterBottom>
                <strong>Story</strong>
              </Typography>
              <Typography variant="body2" gutterBottom>
                {campaign?.story}
              </Typography>

              {campaign?.additionalComment && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2 }} gutterBottom>
                    <strong>Additional Comments</strong>
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {campaign.additionalComment}
                  </Typography>
                </>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Verification Documents</strong>
              </Typography>

              <Grid container spacing={2}>
                {attachments.map((image, index) => (
                  <CardMedia
                    key={index}
                    component="img"
                    image={image}
                    onClick={() => handleImageClick(image)}
                    sx={{
                      width: 200,
                      height: 150,
                      mr: 1,
                      mb: 1,
                      cursor: 'pointer'
                    }}
                  />
                ))}

                {mockDocuments.map((doc) => (
                  <Grid item xs={12} sm={6} key={doc.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.02)'
                        }
                      }}
                      onClick={() => handleImageClick(doc)}
                    >
                      <CardMedia
                        component="img"
                        height="140"
                        image={doc.url}
                        alt={doc.title}
                      />
                      <Typography variant="body2" sx={{ p: 1, textAlign: 'center' }}>
                        {doc.title}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          {(action === 'reject' || action === 'more-info') && (
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={
                  action === 'reject'
                    ? 'Reason for rejection (required)'
                    : 'What information is needed? (required)'
                }
                variant="outlined"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                error={commentError}
                helperText={commentError ? 'This field is required' : ''}
              />
            </Box>
          )}

          <Alert
            severity="warning"
            icon={<Warning fontSize="inherit" />}
            sx={{ mt: 3, mb: 2 }}
          >
            {action === 'approve'
              ? 'This will mark the campaign as verified and make it publicly visible.'
              : action === 'reject'
                ? 'This will reject the campaign verification and keep it private.'
                : action === 'more-info'
                  ? 'This will request more information from the campaign organizer.'
                  : 'Please select an action for this campaign.'}
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant={action === 'approve' ? 'contained' : 'outlined'}
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => handleActionChange('approve')}
              disabled={loading}
            >
              Approve
            </Button>
            <Button
              variant={action === 'more-info' ? 'contained' : 'outlined'}
              color="warning"
              startIcon={<Help />}
              onClick={() => handleActionChange('more-info')}
              disabled={loading}
            >
              Need More Info
            </Button>
            <Button
              variant={action === 'reject' ? 'contained' : 'outlined'}
              color="error"
              startIcon={<Cancel />}
              onClick={() => handleActionChange('reject')}
              disabled={loading}
            >
              Reject
            </Button>
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button
              onClick={onClose}
              disabled={loading}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} /> : null}
              onClick={handleVerify}
              disabled={loading || !action || (commentError && (action === 'reject' || action === 'more-info'))}
            >
              {loading ? 'Processing...' : 'Confirm'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Image Preview Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={handleCloseImage}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Box sx={{ position: 'relative' }}>
            <IconButton
              aria-label="close"
              onClick={handleCloseImage}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
                zIndex: 1
              }}
            >
              <Close />
            </IconButton>
            <Box
              component="img"
              src={selectedImage}
              alt={selectedImage}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            >
            </Box>
          </Box>
          <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
            {selectedImage?.title} 
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VerifyCampaignModal;